import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useForm } from '../../molecules/Form';
import AICampaignService from "../../../services/AICampaignService";
import campaignService from "../../../services/campaignService";
import offerService from "../../../services/offerService";
import Toast from "../../molecules/Toast";
import { languageOptions } from "../../Constant";
import { makeItem, makeItems, personalizationOptions, toneOptions } from "./offerFormConfig";

const hydrateFormFromTaskData = (stepData, form, setters) => {
    const {
        setProofPoints,
        setFeatures,
        setGoals,
        setCallToActions,
        setAllowedGreetings,
        setCoachingPoints,
        setActiveTab,
    } = setters;

    setProofPoints(
        stepData.proof_points?.length
            ? stepData.proof_points.map(value => makeItem({ value }))
            : makeItems(3, { value: '' })
    );
    setFeatures(
        stepData.core_features?.length
            ? stepData.core_features.map(({ pain_point, solution }) =>
                makeItem({ pain_point: pain_point || '', solution: solution || '' }))
            : [makeItem({ pain_point: '', solution: '' })]
    );
    setGoals(
        stepData.outcomes?.length
            ? stepData.outcomes.map(value => makeItem({ value }))
            : makeItems(3, { value: '' })
    );
    setCallToActions(
        stepData.cta?.length
            ? stepData.cta.map(value => makeItem({ value }))
            : [makeItem({ value: '' })]
    );
    setAllowedGreetings(
        stepData.allow_greeting?.length
            ? stepData.allow_greeting.map(value => makeItem({ value }))
            : [makeItem({ value: '' })]
    );
    setCoachingPoints(
        stepData.coaching_points?.length
            ? stepData.coaching_points.map(value => makeItem({ value }))
            : [makeItem({ value: '' })]
    );

    if (stepData.email_length_msg1) {
        setActiveTab(String(stepData.email_length_msg1));
    }

    const formValues = {
        user_company: stepData.user_company,
        company_details: stepData.company_details,
        promoting_in_campaign: stepData.promoting,
        website_url: stepData.company_website,
        select_tone_from_dropdown: stepData.voice_tone
            ? toneOptions.find(t => t.value === stepData.voice_tone) || stepData.voice_tone
            : undefined,
        language_from_dropdown: languageOptions.find(l => l.value === stepData.language) || undefined,
        signature: stepData?.signature,
    };

    personalizationOptions.forEach(option => {
        formValues[option.name] = stepData.personalization_options?.[option.name] ?? false;
    });
    form.setFieldsValue(formValues);
};

const normalizeDropdownValue = value => value?.value ?? value ?? null;

const serializeOfferFormState = ({
    form,
    proofPoints,
    features,
    goals,
    callToActions,
    allowedGreetings,
    coachingPoints,
    activeTab,
}) => {
    const values = form.getFieldsValue();
    return JSON.stringify({
        user_company: values.user_company ?? '',
        company_details: values.company_details ?? '',
        promoting_in_campaign: values.promoting_in_campaign ?? '',
        website_url: values.website_url ?? '',
        tone: normalizeDropdownValue(values.select_tone_from_dropdown),
        language: normalizeDropdownValue(values.language_from_dropdown),
        signature: values.signature ?? '',
        personalization: personalizationOptions.reduce((acc, option) => {
            acc[option.name] = !!values[option.name];
            return acc;
        }, {}),
        proofPoints: proofPoints.map(item => item.value ?? ''),
        features: features.map(item => ({
            pain_point: item.pain_point ?? '',
            solution: item.solution ?? '',
        })),
        goals: goals.map(item => item.value ?? ''),
        callToActions: callToActions.map(item => item.value ?? ''),
        allowedGreetings: allowedGreetings.map(item => item.value ?? ''),
        coachingPoints: coachingPoints.map(item => item.value ?? ''),
        activeTab,
    });
};

const useOfferForm = ({ isEdit = false, offerId = null, defaultEmailLength = '90' } = {}) => {
    const [form] = useForm();
    const location = useLocation();
    const { campaign_id, taskId, list_id } = location.state || {};
    const [autoFillLoading, setAutoFillLoading] = useState(false);
    const [offerLoading, setOfferLoading] = useState(!!offerId);
    const [proofPoints, setProofPoints] = useState(() => makeItems(3, { value: '' }));
    const [features, setFeatures] = useState(() => [makeItem({ pain_point: '', solution: '' })]);
    const [activeTab, setActiveTab] = useState(defaultEmailLength);
    const [goals, setGoals] = useState(() => makeItems(3, { value: '' }));
    const [callToActions, setCallToActions] = useState(() => [makeItem({ value: '' })]);
    const [allowedGreetings, setAllowedGreetings] = useState(() => [makeItem({ value: '' })]);
    const [coachingPoints, setCoachingPoints] = useState(() => [makeItem({ value: '' })]);
    const [hydrated, setHydrated] = useState(false);
    const [canAutoFill, setCanAutoFill] = useState(false);
    const [autoFillFailed, setAutoFillFailed] = useState(false);
    const [formRevision, setFormRevision] = useState(0);
    const autoFillResultRef = useRef(null);
    const initialSnapshotRef = useRef(null);
    const snapshotReadyRef = useRef(false);

    const hydrateSetters = useCallback(() => ({
        setProofPoints,
        setFeatures,
        setGoals,
        setCallToActions,
        setAllowedGreetings,
        setCoachingPoints,
        setActiveTab,
    }), []);

    const getFormSnapshot = useCallback(
        () =>
            serializeOfferFormState({
                form,
                proofPoints,
                features,
                goals,
                callToActions,
                allowedGreetings,
                coachingPoints,
                activeTab,
            }),
        [
            form,
            proofPoints,
            features,
            goals,
            callToActions,
            allowedGreetings,
            coachingPoints,
            activeTab,
        ],
    );

    const handleFormChange = () => {
        const values = form.getFieldsValue();
        setCanAutoFill(!!values.website_url?.trim());
        setFormRevision(prev => prev + 1);
    };

    const markAsSaved = useCallback(() => {
        initialSnapshotRef.current = getFormSnapshot();
    }, [getFormSnapshot]);

    const hasUnsavedChanges = useMemo(() => {
        if (!hydrated || !snapshotReadyRef.current || !initialSnapshotRef.current) {
            return false;
        }
        return getFormSnapshot() !== initialSnapshotRef.current;
    }, [
        hydrated,
        formRevision,
        getFormSnapshot,
        proofPoints,
        features,
        goals,
        callToActions,
        allowedGreetings,
        coachingPoints,
        activeTab,
    ]);

    useEffect(() => {
        if (!hydrated || snapshotReadyRef.current) return;
        snapshotReadyRef.current = true;
        initialSnapshotRef.current = getFormSnapshot();
    }, [
        hydrated,
        getFormSnapshot,
        proofPoints,
        features,
        goals,
        callToActions,
        allowedGreetings,
        coachingPoints,
        activeTab,
    ]);

    const handleAdd = (setter, defaults = { value: '' }) => {
        setter(prev => [...prev, makeItem(defaults)]);
    };

    const handleRemove = (setter, id) => {
        setter(prev => prev.filter(item => item.id !== id));
    };

    const updateItem = (setter, id, updates) => {
        setter(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
    };

    const buildPayload = ({ listId = list_id, campaignId = campaign_id } = {}) => {
        const values = form.getFieldsValue();
        const autoFilled = autoFillResultRef.current;
        return {
            agentId: 2,
            list_id: listId,
            campaignId,
            task_name: values.user_company,
            company_details: values.company_details || autoFilled?.company_details || '',
            // Company Name is hidden on the website path; fall back to the website URL
            // so the required company_name is never empty.
            user_company: values.user_company || values.website_url,
            company_website: values.website_url,
            promoting: values.promoting_in_campaign || autoFilled?.promoting || '',
            outcomes: goals.map(item => item.value).filter(Boolean),
            core_features: features
                .map(item => ({ pain_point: item.pain_point, solution: item.solution }))
                .filter(f => f.pain_point || f.solution),
            proof_points: proofPoints.map(item => item.value).filter(Boolean),
            coaching_points: coachingPoints.map(item => item.value).filter(Boolean),
            personalization_options: personalizationOptions.reduce((acc, option) => {
                acc[option.name] = !!values[option.name];
                return acc;
            }, {}),
            allow_greeting: allowedGreetings.map(item => item.value).filter(Boolean),
            cta: callToActions.map(item => item.value).filter(Boolean),
            email_length_msg1: activeTab === '90' ? 90 : activeTab === '120' ? 120 : 150,
            voice_tone: values.select_tone_from_dropdown?.value || values.select_tone_from_dropdown || null,
            language: values.language_from_dropdown?.value,
            signature: values.signature,
        };
    };

    const autoFillFields = async ({ notifySuccess = true } = {}) => {
        const values = form.getFieldsValue();
        const payload = {
            user_company: values.user_company || values.website_url,
            website_url: values.website_url,
        };

        try {
            setAutoFillLoading(true);
            const response = await AICampaignService.autoFillFields(payload);

            if (!response?.success) {
                throw new Error('Auto-fill returned no data');
            }

            autoFillResultRef.current = response;
            setAutoFillFailed(false);
            if (notifySuccess) {
                Toast({ type: 'success', message: 'Fields auto-filled successfully!' });
            }
            if (response.proof_points?.length) {
                setProofPoints(response.proof_points.map(value => makeItem({ value })));
            }
            if (response.core_features?.length) {
                setFeatures(response.core_features.map(({ pain_point, solution }) =>
                    makeItem({ pain_point: pain_point || '', solution: solution || '' })
                ));
            }
            if (response.outcomes?.length) {
                setGoals(response.outcomes.map(value => makeItem({ value })));
            }
            form.setFieldsValue({ company_details: response.company_details, promoting_in_campaign: response.promoting });
        } catch {
            setAutoFillFailed(true);
            Toast({ type: 'error', message: 'Could not auto-fill from your website. Please add the details manually.' });
        } finally {
            setAutoFillLoading(false);
        }
    };

    useEffect(() => {
        if (!offerId || hydrated) return;

        const loadOffer = async () => {
            try {
                setOfferLoading(true);
                const response = await offerService.getOfferById(offerId);
                if (!response?.success) {
                    throw new Error(response?.message || 'Failed to load offer');
                }
                const stepData = response.data?.dataValues || response.data;
                hydrateFormFromTaskData(stepData, form, hydrateSetters());
                setHydrated(true);
            } catch (error) {
                Toast({ type: 'error', message: error.message });
                setHydrated(true);
            } finally {
                setOfferLoading(false);
            }
        };

        loadOffer();
    }, [offerId, hydrated, form, hydrateSetters]);

    useEffect(() => {
        if (offerId || !isEdit || hydrated || !taskId) return;

        const getAicampaigData = async () => {
            try {
                const response = await campaignService.GetAiCampaign(campaign_id);

                if (response.success) {
                    const stepData = response.data.dataValues;
                    hydrateFormFromTaskData(stepData, form, hydrateSetters());
                    setHydrated(true);
                }
            } catch (error) {
                Toast({ type: 'error', message: error.message });
            } finally {
                setHydrated(true);
            }
        };
        getAicampaigData();
    }, [isEdit, campaign_id, hydrated, form, taskId, offerId, hydrateSetters]);

    return {
        form,
        campaignId: campaign_id,
        taskId,
        offerId,
        listId: list_id,
        offerLoading,
        proofPoints, setProofPoints,
        features, setFeatures,
        goals, setGoals,
        callToActions, setCallToActions,
        allowedGreetings, setAllowedGreetings,
        coachingPoints, setCoachingPoints,
        activeTab, setActiveTab,
        canAutoFill,
        autoFillLoading,
        autoFillFailed,
        handleFormChange,
        handleAdd,
        handleRemove,
        updateItem,
        autoFillFields,
        buildPayload,
        hasUnsavedChanges,
        markAsSaved,
    };
};

export default useOfferForm;
